import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { RAJ_UNITS } from "../const_raj";
import { SearchBox } from "../../../components/Search";
import {
  getGenerationEltrixBackdownDailySourcePlant,
  getReGenerationDailySourceArea,
  getIsgsMetricsDailySourceState,
  getGenerationEltrixBackdownMonthlySourcePlant,
  getReGenerationMonthlySourceArea,
  getIsgsMetricsMonthlySourceState,
} from "../Utility/RestApi";
import { titleCase } from "../../../extensions/extension";
import { getMonthFromDate } from "../../../extensions/date";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const GENERATION_TYPE = ["Interstate", "Intrastate"];

interface RajasthanPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedSource: string[];
  onSourceChange: (items: string[]) => void;
  selectedGenerationType: string[];
  onGenerationTypeChange: (items: string[]) => void;
  selectedState: string[];
  onStateChange: (items: string[]) => void;
  states: string[];
  uniqueSource: string[];
  generationStatus: string[];
  setGenerationStatus: (items: string[]) => void;
  onLoad: any[];
  offLoad: any[];
  selectedBlock: any[];
  setSelectedBlock: any;
  selectedStation: any[];
  setSelectedStation: any;
  uniqueStations: string[];
}

export function RajasthanPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedSource,
  onSourceChange,
  selectedGenerationType,
  onGenerationTypeChange,
  selectedState,
  onStateChange,
  states,
  uniqueSource,
  generationStatus,
  setGenerationStatus,
  onLoad,
  offLoad,
  selectedBlock,
  setSelectedBlock,
  selectedStation,
  setSelectedStation,
  uniqueStations,
}: RajasthanPeakAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [data, setData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);

  const getPlantBaseName = useCallback((fullName: string) => {
    return fullName
      .replace(" Intrastate", "")
      .replace(" Interstate", "")
      .trim();
  }, []);

  const getMonthWithYear = useCallback((month: number, year: number) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const yearShort = year.toString().slice(-2);
    return `${monthNames[month - 1]}-${yearShort}`;
  }, []);

  const parseMonthYear = useCallback(
    (monthYearStr: string | undefined): { month: number; year: number } => {
      if (!monthYearStr) return { month: 0, year: 0 };
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const parts = monthYearStr.split("-");
      if (parts.length !== 2) return { month: 0, year: 0 };
      const [monthStr, yearStr] = parts;
      const month = monthNames.indexOf(monthStr) + 1;
      const year = 2000 + parseInt(yearStr);
      return { month, year };
    },
    []
  );

  const makeCategoryKey = useCallback(
    (item: any) => {
      const raw = (
        item?.plant_name ||
        item?.station ||
        item?.unique_id ||
        ""
      ).toString();
      const base = getPlantBaseName(raw);
      return base
        .toString()
        .replace(/\s+/g, "_")
        .replace(/[()]/g, "")
        .replace(/[^a-zA-Z0-9_\-]/g, "")
        .toLowerCase();
    },
    [getPlantBaseName]
  );

  const fetchPeakData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      let allData: any[] = [];

      const statesToSend = selectedState.length > 0 ? selectedState : undefined;
      const utilitiesToSend =
        selectedStation.length > 0
          ? selectedStation.map((s: any) =>
              getPlantBaseName(s?.plant_name || s?.value || s)
            )
          : undefined;

      const isRajasthanSelected =
        selectedState.length === 0 ||
        selectedState.some((s) =>
          s.toString().toLowerCase().includes("rajasthan")
        );

      const plantNamesToSend =
        selectedStation.length > 0
          ? selectedStation.map((s: any) => {
              const plantName = s?.plant_name || s?.value || s;
              return getPlantBaseName(plantName);
            })
          : undefined;

      const reAreasToSend: string[] = [];
      if (
        selectedGenerationType.length === 0 ||
        selectedGenerationType.some(
          (type) => type.toLowerCase() === "intrastate"
        )
      ) {
        reAreasToSend.push("IntraState");
      }
      if (
        selectedGenerationType.length === 0 ||
        selectedGenerationType.some(
          (type) => type.toLowerCase() === "interstate"
        )
      ) {
        reAreasToSend.push("InterState");
      }

      const [isgsRes, intraRes, reRes] = await Promise.all([
        selectedState.length > 0 || selectedStation.length > 0
          ? selectedXaxis.key === "month"
            ? getIsgsMetricsMonthlySourceState(
                startDateStr,
                endDateStr,
                selectedSource.length > 0 ? selectedSource : undefined,
                statesToSend,
                utilitiesToSend
              )
            : getIsgsMetricsDailySourceState(
                startDateStr,
                endDateStr,
                selectedSource.length > 0 ? selectedSource : undefined,
                statesToSend,
                utilitiesToSend
              )
          : Promise.resolve(null),
        selectedStation.length > 0 ||
        (selectedState.length > 0 && isRajasthanSelected)
          ? selectedXaxis.key === "month"
            ? getGenerationEltrixBackdownMonthlySourcePlant(
                startDateStr,
                endDateStr,
                selectedSource,
                plantNamesToSend
              )
            : getGenerationEltrixBackdownDailySourcePlant(
                startDateStr,
                endDateStr,
                selectedSource,
                plantNamesToSend
              )
          : Promise.resolve(null),
        reAreasToSend.length > 0
          ? selectedXaxis.key === "date"
            ? getReGenerationDailySourceArea(
                startDateStr,
                endDateStr,
                selectedSource.length > 0 ? selectedSource : undefined,
                reAreasToSend
              )
            : getReGenerationMonthlySourceArea(
                startDateStr,
                endDateStr,
                selectedSource.length > 0 ? selectedSource : undefined,
                reAreasToSend
              )
          : Promise.resolve(null),
      ]);

      if (isgsRes && Array.isArray(isgsRes)) {
        const flattenedIsgsData = isgsRes.flatMap((sourceItem) => {
          const results: any[] = [];

          if (sourceItem.states && Array.isArray(sourceItem.states)) {
            sourceItem.states.forEach((stateData: any) => {
              if (stateData.utilities && Array.isArray(stateData.utilities)) {
                stateData.utilities.forEach((utility: any) => {
                  const uniqueId = `${utility.utility_name}_${utility.generation_source}_${utility.state_name}`;

                  // Support both daily and monthly utility payload shapes
                  const isMonthly =
                    !!(utility.year || utility.month) && !utility.date;
                  const dateVal = isMonthly
                    ? getMonthWithYear(utility.month, utility.year)
                    : utility.date;
                  const monthVal = isMonthly
                    ? getMonthWithYear(utility.month, utility.year)
                    : getMonthFromDate(utility.date);

                  results.push({
                    date: dateVal,
                    month: monthVal,
                    source: titleCase(utility.generation_source),
                    sector: "Interstate",
                    station: `${utility.utility_name} (${titleCase(
                      utility.generation_source
                    )})`,
                    plant_name: utility.utility_name,
                    state: utility.state_name || utility.state,
                    unique_id: uniqueId,
                    isRE: false,
                    isPlantLevel: true,
                    peak_generation: isMonthly
                      ? utility.peak_value_mw ||
                        utility.peak_daily_value_mw ||
                        0
                      : utility.daily_peak_value_mw,
                    peak_capacity: 0,
                    peak_backdown: 0,
                    morning_generation: isMonthly
                      ? utility.morning_peak_value_mw || 0
                      : utility.formatted_data?.find(
                          (p: any) => p.time_period === "Morning"
                        )?.max_value || 0,
                    morning_capacity: 0,
                    morning_backdown: 0,
                    evening_generation: isMonthly
                      ? utility.evening_peak_value_mw || 0
                      : utility.formatted_data?.find(
                          (p: any) => p.time_period === "Evening"
                        )?.max_value || 0,
                    evening_capacity: 0,
                    evening_backdown: 0,
                    average_generation: isMonthly
                      ? utility.average_value_mw ||
                        utility.average_daily_value_mw ||
                        0
                      : utility.avg_daily_value_mw,
                    average_capacity: 0,
                    average_backdown: 0,
                  });
                });
              }
            });
          }

          return results;
        });
        allData = [...allData, ...flattenedIsgsData];
      }

      if (intraRes && Array.isArray(intraRes)) {
        const flattenedIntraData = intraRes.flatMap((sourceItem) => {
          const isRE = ["hydro", "solar", "wind", "biomass"].includes(
            sourceItem.generation_source.toLowerCase()
          );
          return sourceItem.plants.map((plant: any) => {
            const uniqueId = `${plant.plant_name}_${sourceItem.generation_source}_Intrastate`;
            const isMonthly = !!(plant.year || plant.month) && !plant.date;

            return {
              date: isMonthly
                ? getMonthWithYear(plant.month, plant.year)
                : plant.date,
              month: isMonthly
                ? getMonthWithYear(plant.month, plant.year)
                : getMonthFromDate(plant.date),
              source: titleCase(sourceItem.generation_source),
              sector: "Intrastate",
              station: `${plant.plant_name} (${titleCase(
                sourceItem.generation_source
              )})`,
              plant_name: plant.plant_name,
              unique_id: uniqueId,
              isRE: isRE,
              isPlantLevel: true,
              peak_generation: isMonthly
                ? plant.peak_onbards_mw
                : plant.daily_peak_onbards_mw,
              peak_capacity: isMonthly
                ? plant.peak_onbardc_mw
                : plant.daily_peak_onbardc_mw,
              peak_backdown: isMonthly
                ? plant.peak_backdown_mw
                : plant.daily_peak_backdown_mw,
              morning_generation: isMonthly
                ? plant.morning_peak_onbards_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Morning"
                  )?.max_onbards || 0,
              morning_capacity: isMonthly
                ? plant.morning_peak_onbardc_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Morning"
                  )?.max_onbardc || 0,
              morning_backdown: isMonthly
                ? plant.morning_peak_backdown_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Morning"
                  )?.max_backdown || 0,
              evening_generation: isMonthly
                ? plant.evening_peak_onbards_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Evening"
                  )?.max_onbards || 0,
              evening_capacity: isMonthly
                ? plant.evening_peak_onbardc_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Evening"
                  )?.max_onbardc || 0,
              evening_backdown: isMonthly
                ? plant.evening_peak_backdown_mw
                : plant.formatted_data?.find(
                    (p: any) => p.time_period === "Evening"
                  )?.max_backdown || 0,
              average_generation: isMonthly
                ? plant.average_onbards_mw
                : plant.avg_daily_onbards_mw,
              average_capacity: isMonthly
                ? plant.average_onbardc_mw
                : plant.avg_daily_onbardc_mw,
              average_backdown: isMonthly
                ? plant.average_backdown_mw
                : plant.avg_daily_backdown_mw,
            };
          });
        });
        allData = [...allData, ...flattenedIntraData];
      }

      if (reRes && Array.isArray(reRes)) {
        const flattenedReData = reRes.flatMap((item: any) => {
          const results: any[] = [];
          const sourceName =
            item.energy_type ||
            (selectedSource.length === 1 ? selectedSource[0] : "RE");
          const isMonthly = !!(item.year || item.month) && !item.date;

          results.push({
            date: isMonthly
              ? getMonthWithYear(item.month, item.year)
              : item.date,
            month: isMonthly
              ? getMonthWithYear(item.month, item.year)
              : getMonthFromDate(item.date),
            source: titleCase(sourceName),
            sector: "RE",
            station: titleCase(sourceName),
            plant_name: "Aggregate",
            unique_id: `${sourceName}_Aggregate_RE`,
            isSource: true,
            isRE: true,
            isPlantLevel: false,
            state: item.state || item.region || "Rajasthan",
            peak_generation: isMonthly
              ? item.source_peak_value_mw
              : item.source_peak_daily_value_mw,
            peak_capacity: 0,
            peak_backdown: 0,
            morning_generation: isMonthly
              ? item.morning_peak_value_mw || 0
              : 0,
            morning_capacity: 0,
            morning_backdown: 0,
            evening_generation: isMonthly
              ? item.evening_peak_value_mw || 0
              : 0,
            evening_capacity: 0,
            evening_backdown: 0,
            average_generation: isMonthly
              ? item.source_average_value_mw
              : item.source_avg_daily_value_mw,
            average_capacity: 0,
            average_backdown: 0,
          });

          if (item.areas && Array.isArray(item.areas)) {
            item.areas.forEach((areaData: any) => {
              const areaName = areaData.area;
              const sectorName =
                areaName === "IntraState"
                  ? "Intrastate"
                  : areaName === "InterState"
                  ? "Interstate"
                  : areaName;

              const areaState =
                areaData.state ||
                areaData.state_name ||
                areaData.stateCode ||
                areaData.state_code ||
                (areaName &&
                areaName !== "IntraState" &&
                areaName !== "InterState"
                  ? areaName
                  : undefined);

              const sourceLabel = (sourceName || "").toString().trim();
              const showSourceLabel =
                sourceLabel !== "" && sourceLabel.toLowerCase() !== "re";

              let stationLabel: string;
              if (areaState) {
                stationLabel = showSourceLabel
                  ? `${titleCase(sourceLabel)} (${areaState})`
                  : `${areaState}`;
              } else {
                stationLabel = showSourceLabel
                  ? `${titleCase(sourceLabel)} (${areaName})`
                  : `${areaName}`;
              }

              const plantName = areaState || areaName;
              const uniqueId = `${sourceName}_${plantName}_${areaName}`;

              results.push({
                date: isMonthly
                  ? getMonthWithYear(areaData.month, areaData.year)
                  : areaData.date,
                month: isMonthly
                  ? getMonthWithYear(areaData.month, areaData.year)
                  : getMonthFromDate(areaData.date),
                source: titleCase(sourceName),
                sector: sectorName,
                station: stationLabel,
                plant_name: plantName,
                unique_id: uniqueId,
                isSource: false,
                isRE: true,
                isPlantLevel: false,
                state: areaState || item.state || "Rajasthan",
                peak_generation: isMonthly
                  ? areaData.peak_value_mw
                  : areaData.daily_peak_value_mw,
                peak_capacity: 0,
                peak_backdown: 0,
                morning_generation: isMonthly
                  ? areaData.morning_peak_value_mw || 0
                  : areaData.formatted_data?.find(
                      (p: any) => p.time_period === "Morning"
                    )?.max_value || 0,
                morning_capacity: 0,
                morning_backdown: 0,
                evening_generation: isMonthly
                  ? areaData.evening_peak_value_mw || 0
                  : areaData.formatted_data?.find(
                      (p: any) => p.time_period === "Evening"
                    )?.max_value || 0,
                evening_capacity: 0,
                evening_backdown: 0,
                average_generation: isMonthly
                  ? areaData.average_value_mw
                  : areaData.avg_daily_value_mw,
                average_capacity: 0,
                average_backdown: 0,
              });
            });
          }
          return results;
        });
        allData = [...allData, ...flattenedReData];
      }

      // Debug: log RE Interstate items to help diagnose missing Interstate RE data
      try {
        const reInterStateItems = allData.filter(
          (i) =>
            i.isRE && (i.sector || "").toString().toLowerCase() === "interstate"
        );
        console.debug(
          "[OverviewPeak] fetched allData count:",
          allData.length,
          "RE InterState count:",
          reInterStateItems.length
        );
        console.debug(
          "[OverviewPeak] RE InterState samples:",
          reInterStateItems.slice(0, 6).map((it) => ({
            unique_id: it.unique_id,
            station: it.station,
            plant_name: it.plant_name,
            date: it.date,
            month: it.month,
          }))
        );
      } catch (e) {
        console.warn("[OverviewPeak] debug log failed", e);
      }

      setData(allData);
    } catch (error) {
      console.error("Error fetching peak data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    getMonthWithYear,
    getPlantBaseName,
    selectedGenerationType,
    selectedSource,
    selectedStation,
    selectedState,
    selectedXaxis.key,
  ]);

  useEffect(() => {
    fetchPeakData();
  }, [fetchPeakData]);

  useEffect(() => {
    let filteredData = data;

    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(item.source)
      );
    }

    if (selectedGenerationType.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedGenerationType.includes(item.sector)
      );
    }

    if (selectedState.length > 0) {
      const selectedStateLower = selectedState.map((s) =>
        (s || "").toString().trim().toLowerCase()
      );
      filteredData = filteredData.filter((item) => {
        const st =
          item.state ||
          item.plant_state ||
          item.state_name ||
          item.region ||
          "";
        return selectedStateLower.includes(
          (st || "").toString().trim().toLowerCase()
        );
      });
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.slot)
      );
    }

    if (selectedStation.length > 0) {
      const selectedPlantNames = selectedStation.map((s: any) => {
        // Handle both object and string formats
        const plantName = s?.plant_name || s?.value || s;
        return getPlantBaseName(plantName);
      });
      filteredData = filteredData.filter((item) => {
        const itemPlantName = getPlantBaseName(item.plant_name || "");
        return selectedPlantNames.includes(itemPlantName);
      });
    }

    filteredData = [...filteredData].sort((a, b) => {
      if (selectedXaxis.key === "month") {
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        if (aMonthYear.year !== bMonthYear.year)
          return aMonthYear.year - bMonthYear.year;
        return aMonthYear.month - bMonthYear.month;
      } else {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const showPlantLevel = selectedStation.length > 0 || selectedState.length > 0;

    if (showPlantLevel) {
      // Include plant-level data and all RE items (excluding aggregates if possible)
      const plantLevelData = filteredData.filter(
        (item) => item.isPlantLevel === true || (item.isRE === true && item.isSource !== true)
      );

      const convertedData = plantLevelData.map((item) => ({
        ...item,
        peak_generation: (item.peak_generation || 0) * conversionFactor,
        peak_capacity: (item.peak_capacity || 0) * conversionFactor,
        peak_backdown: (item.peak_backdown || 0) * conversionFactor,
        morning_generation: (item.morning_generation || 0) * conversionFactor,
        morning_capacity: (item.morning_capacity || 0) * conversionFactor,
        morning_backdown: (item.morning_backdown || 0) * conversionFactor,
        evening_generation: (item.evening_generation || 0) * conversionFactor,
        evening_capacity: (item.evening_capacity || 0) * conversionFactor,
        evening_backdown: (item.evening_backdown || 0) * conversionFactor,
        average_generation: (item.average_generation || 0) * conversionFactor,
        average_capacity: (item.average_capacity || 0) * conversionFactor,
        average_backdown: (item.average_backdown || 0) * conversionFactor,
      }));

      setChartData(convertedData);
      setTableData(convertedData);
    } else {
      const groupedData: { [key: string]: any } = {};
      const sourceAreaData = filteredData.filter(
        (item) => item.isPlantLevel !== true
      );

      sourceAreaData.forEach((item) => {
        // Keep RE source-level aggregates (isSource && isRE) so Interstate RE
        // aggregate entries are available for grouping. Skip only non-RE sources.
        if (item.isSource && !item.isRE) return;

        const groupingIdentifier = (() => {
          // For ISGS (non-RE) data, group by state if state is selected
          if (!item.isRE) {
            if (
              selectedState.length > 0 &&
              item.state &&
              item.state.toString().trim().toLowerCase() !== "rajasthan"
            ) {
              return `${item.source} (${item.state})`;
            }
            return item.source;
          }
          // For RE data
          if (selectedStation.length > 0) return item.unique_id;
          if (
            selectedState.length > 0 &&
            item.state &&
            item.state.toString().trim().toLowerCase() !== "rajasthan"
          ) {
            return `${item.source} (${item.state})`;
          }
          return item.station;
        })();

        const groupKey =
          selectedXaxis.key === "date"
            ? `${item.date}-${groupingIdentifier}`
            : `${item.month}-${groupingIdentifier}`;

        if (!groupedData[groupKey]) {
          groupedData[groupKey] = {
            date: item.date,
            month: item.month,
            source: item.source,
            sector: item.sector,
            station: groupingIdentifier,
            unique_id: item.unique_id,
            isRE: item.isRE,
            state: item.state,
            peak_generation: 0,
            peak_capacity: 0,
            peak_backdown: 0,
            morning_generation: 0,
            morning_capacity: 0,
            morning_backdown: 0,
            evening_generation: 0,
            evening_capacity: 0,
            evening_backdown: 0,
            average_generation: 0,
            average_capacity: 0,
            average_backdown: 0,
            count: 0,
          };
        }

        groupedData[groupKey].peak_generation +=
          (item.peak_generation || 0) * conversionFactor;
        groupedData[groupKey].peak_capacity +=
          (item.peak_capacity || 0) * conversionFactor;
        groupedData[groupKey].peak_backdown +=
          (item.peak_backdown || 0) * conversionFactor;
        groupedData[groupKey].morning_generation +=
          (item.morning_generation || 0) * conversionFactor;
        groupedData[groupKey].morning_capacity +=
          (item.morning_capacity || 0) * conversionFactor;
        groupedData[groupKey].morning_backdown +=
          (item.morning_backdown || 0) * conversionFactor;
        groupedData[groupKey].evening_generation +=
          (item.evening_generation || 0) * conversionFactor;
        groupedData[groupKey].evening_capacity +=
          (item.evening_capacity || 0) * conversionFactor;
        groupedData[groupKey].evening_backdown +=
          (item.evening_backdown || 0) * conversionFactor;
        groupedData[groupKey].average_generation +=
          (item.average_generation || 0) * conversionFactor;
        groupedData[groupKey].average_capacity +=
          (item.average_capacity || 0) * conversionFactor;
        groupedData[groupKey].average_backdown +=
          (item.average_backdown || 0) * conversionFactor;
        groupedData[groupKey].count += 1;
      });

      const convertedData = Object.values(groupedData);
      setChartData(convertedData);
      setTableData(convertedData);
    }
  }, [
    data,
    selectedUnit,
    selectedSource,
    selectedGenerationType,
    selectedState,
    selectedBlock,
    selectedStation,
    selectedXaxis.key,
    getPlantBaseName,
    parseMonthYear,
  ]);

  const legends = useMemo(() => {
    const isSpecialView =
      selectedStation.length > 0 ||
      selectedState.length > 0 ||
      ((selectedXaxis.key === "date" || selectedXaxis.key === "month") &&
        chartData.some((i) => i.isRE));

    if (isSpecialView) {
      const sourceForCategories =
        selectedStation.length > 0 || selectedState.length > 0
          ? chartData
          : chartData.filter((i) => i.isRE);

      // Build a mapping from canonical key -> station label so we can show
      // friendly names while using a normalized key for data joins.
      const keyToLabel = new Map(
        chartData
          .map(
            (i) =>
              [
                makeCategoryKey(i),
                i.station || i.plant_name || i.unique_id,
              ] as const
          )
          .filter(([k]) => k !== undefined && k !== null)
      );

      const uniqueCategories = Array.from(
        new Set(sourceForCategories.map((item) => makeCategoryKey(item)))
      );

      const showGeneration =
        generationStatus.length === 0 ||
        generationStatus.some((v) => v.toLowerCase().includes("generation"));
      const showCapacity =
        generationStatus.length === 0 ||
        generationStatus.some(
          (v) =>
            v.toLowerCase().includes("capacity") ||
            v.toLowerCase().includes("declared")
        );
      const showBackdown =
        generationStatus.length === 0 ||
        generationStatus.some((v) => v.toLowerCase().includes("backdown"));

      const dynamicLegends: any[] = [];

      uniqueCategories.forEach((catName) => {
        const categoryItem = chartData.find(
          (item) => makeCategoryKey(item) === catName
        );
        const isRE = categoryItem?.isRE && selectedStation.length === 0;
        const mappedName =
          (keyToLabel.get(catName) as string) || categoryItem?.station;
        // Always prefer the human-friendly station label for legend display
        // even when pivoting by the canonical key. Keep `catName`
        // as the `dataKey` so chart mapping still works.
        const displayName = mappedName || catName;

        const genSuffix = isRE ? "" : " - Generation";
        const capSuffix = " - Capacity";
        const backSuffix = " - Backdown";

        if (showGeneration || isRE) {
          dynamicLegends.push({
            dataKey: `${catName}_peak_generation`,
            name: `${displayName}${genSuffix} - Daily Peak`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "peak_gen" : undefined,
          });
        }

        if (!isRE) {
          if (showCapacity)
            dynamicLegends.push({
              dataKey: `${catName}_peak_capacity`,
              name: `${displayName}${capSuffix} - Daily Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "peak_cap"
                  : undefined,
            });
          if (showBackdown)
            dynamicLegends.push({
              dataKey: `${catName}_peak_backdown`,
              name: `${displayName}${backSuffix} - Daily Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "peak_back"
                  : undefined,
            });
        }

        if (showGeneration || isRE) {
          dynamicLegends.push({
            dataKey: `${catName}_morning_generation`,
            name: `${displayName}${genSuffix} - Morning Peak`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined
                ? "morning_gen"
                : undefined,
          });
        }
        if (!isRE) {
          if (showCapacity)
            dynamicLegends.push({
              dataKey: `${catName}_morning_capacity`,
              name: `${displayName}${capSuffix} - Morning Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "morning_cap"
                  : undefined,
            });
          if (showBackdown)
            dynamicLegends.push({
              dataKey: `${catName}_morning_backdown`,
              name: `${displayName}${backSuffix} - Morning Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "morning_back"
                  : undefined,
            });
        }

        if (showGeneration || isRE) {
          dynamicLegends.push({
            dataKey: `${catName}_evening_generation`,
            name: `${displayName}${genSuffix} - Evening Peak`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined
                ? "evening_gen"
                : undefined,
          });
        }
        if (!isRE) {
          if (showCapacity)
            dynamicLegends.push({
              dataKey: `${catName}_evening_capacity`,
              name: `${displayName}${capSuffix} - Evening Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "evening_cap"
                  : undefined,
            });
          if (showBackdown)
            dynamicLegends.push({
              dataKey: `${catName}_evening_backdown`,
              name: `${displayName}${backSuffix} - Evening Peak`,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? "evening_back"
                  : undefined,
            });
        }
      });

      return dynamicLegends;
    }

    const showGeneration =
      generationStatus.length === 0 ||
      generationStatus.some((v) => v.toLowerCase().includes("generation"));
    const showCapacity =
      generationStatus.length === 0 ||
      generationStatus.some(
        (v) =>
          v.toLowerCase().includes("capacity") ||
          v.toLowerCase().includes("declared")
      );
    const showBackdown =
      generationStatus.length === 0 ||
      generationStatus.some((v) => v.toLowerCase().includes("backdown"));

    const defaultLegends: any[] = [];

    if (showGeneration) {
      defaultLegends.push(
        {
          dataKey: "peak_generation",
          name: "Generation - Daily Peak",
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "peak" : undefined,
        },
        {
          dataKey: "morning_generation",
          name: "Generation - Morning Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "morning" : undefined,
        },
        {
          dataKey: "evening_generation",
          name: "Generation - Evening Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "evening" : undefined,
        }
      );
    }

    if (showCapacity) {
      defaultLegends.push(
        {
          dataKey: "peak_capacity",
          name: "Capacity - Daily Peak",
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "peak" : undefined,
        },
        {
          dataKey: "morning_capacity",
          name: "Capacity - Morning Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "morning" : undefined,
        },
        {
          dataKey: "evening_capacity",
          name: "Capacity - Evening Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "evening" : undefined,
        }
      );
    }

    if (showBackdown) {
      defaultLegends.push(
        {
          dataKey: "peak_backdown",
          name: "Backdown - Daily Peak",
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "peak" : undefined,
        },
        {
          dataKey: "morning_backdown",
          name: "Backdown - Morning Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "morning" : undefined,
        },
        {
          dataKey: "evening_backdown",
          name: "Backdown - Evening Peak",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "evening" : undefined,
        }
      );
    }

    return defaultLegends;
  }, [
    selectedXaxis.key,
    chartData,
    selectedChartType,
    generationStatus,
    selectedStation,
    selectedState,
    makeCategoryKey,
  ]);

  const transformedChartData = useMemo(() => {
    // Show plant/source broken down view only when a station or state is selected or when RE categories exist
    const isSpecialView =
      selectedStation.length > 0 ||
      selectedState.length > 0 ||
      ((selectedXaxis.key === "date" || selectedXaxis.key === "month") &&
        chartData.some((i) => i.isRE));

    if (isSpecialView) {
      // pivot by unique_id when a station/state is selected
      const dateMap: { [key: string]: any } = {};

      // Determine which categories should be pivoted: all when filtered, otherwise only RE categories
      const categorySet = new Set(
        (selectedStation.length > 0 || selectedState.length > 0
          ? chartData
          : chartData.filter((i) => i.isRE)
        ).map((i) => makeCategoryKey(i))
      );

      chartData.forEach((item) => {
        const dateKey = selectedXaxis.key === "month" ? item.month : item.date;
        const catName = makeCategoryKey(item);

        // Skip non-pivoted categories when we're in RE-only pivot mode
        if (!categorySet.has(catName)) return;

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: item.date,
            month: item.month,
          };
        }

        // Pivot metrics into category-specific keys
        dateMap[dateKey][`${catName}_peak_generation`] = item.peak_generation;
        dateMap[dateKey][`${catName}_peak_capacity`] = item.peak_capacity;
        dateMap[dateKey][`${catName}_peak_backdown`] = item.peak_backdown;
        dateMap[dateKey][`${catName}_morning_generation`] =
          item.morning_generation;
        dateMap[dateKey][`${catName}_morning_capacity`] = item.morning_capacity;
        dateMap[dateKey][`${catName}_morning_backdown`] = item.morning_backdown;
        dateMap[dateKey][`${catName}_evening_generation`] =
          item.evening_generation;
        dateMap[dateKey][`${catName}_evening_capacity`] = item.evening_capacity;
        dateMap[dateKey][`${catName}_evening_backdown`] = item.evening_backdown;
        dateMap[dateKey][`${catName}_average_generation`] =
          item.average_generation;
      });

      return Object.values(dateMap);
    }

    return chartData;
  }, [
    chartData,
    selectedXaxis.key,
    selectedStation,
    selectedState,
    makeCategoryKey,
  ]);

  const [activeLegends, setActiveLegends] = useState<any[]>([]);

  useEffect(() => {
    setActiveLegends(legends);
  }, [legends]);



  const { visibleLegends, handleLegendSelect, selectedLegends } =
     useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: transformedChartData,
    tableData: tableData,
    unit: selectedUnit,
    extraKeys: selectedXaxis.key === "date" ? ["date"] : ["month"],
    extraLabels: selectedXaxis.key === "date" ? ["Date"] : ["Month"],
    filename: AddDatesWithFileName({
      fileName: "Generation Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={RAJ_UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Variable"
            items={onLoad.concat(offLoad).map((item) => item.label)}
            selectedItems={generationStatus}
            setSelectedItems={setGenerationStatus}
          />

          <FilterMenu
            title="Source"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={onSourceChange}
          />

          <FilterMenu
            title="Area"
            items={GENERATION_TYPE}
            setSelectedItems={onGenerationTypeChange}
            selectedItems={selectedGenerationType}
          />

          <FilterMenu
            title="State"
            items={states}
            selectedItems={selectedState}
            setSelectedItems={onStateChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Peak Analysis" subtitle="Fetching peak generation data by source & station…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">
              Generation Peak Analysis ({selectedUnit})
              {selectedSource.length === 1 && ` - ${selectedSource[0]}`}
            </h2>
            <div className="flex gap-x-2 w-full justify-end">
              {/* <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              /> */}

              <SearchBox
                options={uniqueStations
                  .filter((item) => !item.includes("RE IntraState"))
                  .map((item) => {
                    const plantName = getPlantBaseName(item);
                    return {
                      value: item,
                      label: item,
                      plant_name: plantName,
                    };
                  })}
                isMany={true}
                placeholder="Search Station"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV({
                      ...tablePar,
                    });
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(tablePar);
                  } else if (dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              key={`peak-${selectedUnit}-${selectedXaxis.key}-${
                selectedChartType.value
              }-${selectedSource.join(",")}`}
              data={transformedChartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={legends as any}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey={selectedXaxis.key}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  Intrastate generation : ELTRIX Backdown Reports, Intrastate RE
                  generation : NREB Grid Curves Interstate, generation : Net
                  schedule report breakup ISGS (WBES), Interstate RE generation:
                  Net schedule report breakup OA REMC (WBES)
                </p>
              }
              text="1. Generation = Scheduled Generation for Intrastate Conventional Generating Stations.
2. Capacity (Declared): Declared capacity for Intrastate Conventional Generating Stations.
3. Backdown = Difference between Capacity Declared and Generation.
4. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
